import React, { useMemo, useCallback, useState, useRef, memo } from 'react'
import { Box, Flex } from '@chakra-ui/core'
import { FixedSizeList as List } from 'react-window'
import useSelection, { SelectionMode } from '../OptimizedGroupsTree/hooks/useSelection'
import { RootState } from 'constants/interfaces'
import { useSelector } from 'react-redux'
import { UserRow } from './UserRow'
import { NoResults } from '../GroupsTree/NoResults'
import { SearchInput } from 'components/TasksV2/Insights/Content/ContextsList/SearchInput'
import { NoFocusButton } from '../NoFocusButton'
import { isRtlSelector } from 'redux/selectors/general'
import colors from 'constants/colors'
import useLocaleText from 'components/useLocaleText'
export const ROW_HEIGHT = 60

interface UsersListProps {
  showSearch?: boolean
  selectionMode?: SelectionMode
  initialSelectedIds?: string[]
  listHeight?: number
  onSelectionChange?: (selectedIds: string[]) => void
  isShowTags?: boolean
}

const UsersList: React.FC<UsersListProps> = memo(
  ({
    showSearch = true,
    selectionMode = SelectionMode.MULTIPLE,
    initialSelectedIds = [],
    listHeight = 600,
    onSelectionChange,
    isShowTags = false,
  }) => {
    const isRtl = useSelector(isRtlSelector)
    const {
      retail_users: users,
      retailUsersObject,
      mappedGroupsByUsers,
    } = useSelector((state: RootState) => state.config.config)
    const [searchTerm, setSearchTerm] = useState('')
    const listRef = useRef<List>(null)
    const t_select_all = useLocaleText('select_all_stores_button')
    const t_clear = useLocaleText('clear_stores_button')

    const selectionOptions = useMemo(
      () => ({
        mode: selectionMode,
        initialSelectedIds,
        allIds: Object.keys(retailUsersObject || {}),
        onSelectionChange,
      }),
      [selectionMode, initialSelectedIds, retailUsersObject, onSelectionChange]
    )

    const { toggleSelection, isSelected, selectAll, clearSelection, isAllSelected } = useSelection(selectionOptions)

    const filteredUsers = useMemo(() => {
      if (!searchTerm.trim()) return users || []

      const lowerSearchTerm = searchTerm.toLowerCase()
      return (users || []).filter((user) => {
        const userGroup = mappedGroupsByUsers?.[user.uid]?.[0] ?? ''

        return (
          (user.first_name || '').toLowerCase().includes(lowerSearchTerm) ||
          (user.last_name || '').toLowerCase().includes(lowerSearchTerm) ||
          userGroup.toLowerCase().includes(lowerSearchTerm)
        )
      })
    }, [users, searchTerm, mappedGroupsByUsers])

    const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value)
    }, [])

    const toggleSelectAll = useCallback(() => {
      if (isAllSelected) {
        clearSelection()
      } else {
        selectAll()
      }
    }, [isAllSelected, selectAll, clearSelection])

    const Row = useCallback(
      ({ index, style }: { index: number; style: React.CSSProperties }) => {
        const user = filteredUsers[index]
        if (!user) return null

        return (
          <UserRow
            user={user}
            isSelected={isSelected(user.uid)}
            toggleSelection={toggleSelection}
            style={style}
            key={user.uid}
            isShowTags={isShowTags}
            selectionMode={selectionMode}
          />
        )
      },
      [filteredUsers, isSelected, isShowTags, selectionMode, toggleSelection]
    )

    return (
      <Box>
        {showSearch && (
          <Flex w="100%" flexDir="row" alignItems="center" p={2} justifyContent="space-between" style={{ gap: 6 }}>
            <SearchInput
              w="100%"
              onChange={handleSearch}
              value={searchTerm}
              borderRadius="5px"
              border="1px solid #e0e0e0"
            />
            {selectionMode === SelectionMode.MULTIPLE && !searchTerm && (
              <NoFocusButton
                w="max-content"
                minWidth="100px"
                onClick={toggleSelectAll}
                borderRadius="5px"
                fontWeight="normal"
                borderColor={colors.greyLight}
              >
                {isAllSelected ? t_clear : t_select_all}
              </NoFocusButton>
            )}
          </Flex>
        )}

        <Box h={listHeight}>
          {filteredUsers.length > 0 ? (
            <List
              ref={listRef}
              direction={isRtl ? 'rtl' : 'ltr'}
              height={listHeight}
              itemCount={filteredUsers.length}
              itemSize={ROW_HEIGHT}
              width="100%"
              itemKey={(index) => filteredUsers[index]?.uid || index}
            >
              {Row}
            </List>
          ) : (
            <NoResults />
          )}
        </Box>
      </Box>
    )
  }
)

export default UsersList
